/**
 * shared/config.ts — one env namespace, `VOX_`, parsed and validated at boot.
 *
 * ⚠️ IT FAILS THE PROCESS ON AN UNKNOWN `VOX_*` KEY (§12.4). A typo'd variable that is silently
 * ignored is a setting somebody believes is on. audiobook's subsystem designs shipped
 * `AB_ENSURE_ANON_HOUR` and `ANON_ENSURE_QUOTA` for the same knob, and `MEDIA_CACHE_BYTES` and
 * `AB_MEDIA_CACHE_BYTES` for the same directory; one would have been read and the other silently
 * ignored. If you need a new variable, ADD IT TO `KNOWN_KEYS` FIRST — that is the intended workflow,
 * not an obstacle.
 *
 * ⚠️ It does NOT fail on a missing secret. `EnvironmentFile=-/etc/voice-karti/env` has a load-bearing
 * leading dash: a missing secrets file must bring the box up in degraded mode so you can see the site
 * well enough to fix it, rather than making the unit fail to start. Missing secrets land in
 * `warnings[]`, which the boot log prints and `/api/v1/status` reports. Degraded and visible; never
 * degraded and silent.
 *
 * `loadConfig()` takes the environment as an ARGUMENT and touches no globals, because `shared/` is
 * bundled by Vite and must contain no I/O and no `node:` imports. The client learns everything it
 * needs from `GET /api/v1/session` (`SessionEnvelope.app` in shared/protocol.ts).
 */

// ─── the shape ───────────────────────────────────────────────────────────────

export type VoxEnv = "dev" | "prod";

/**
 * `live` probes spark-1 and offers the reveal. `none` says up front that the computer that makes the
 * voice is asleep and offers the deferred path as a deliberate choice (§3.14).
 *
 * ⚠️ **`none` is not a degraded mode, it is a supported product state**, and it is what P1 runs in:
 * P1's gate is "Karti's mother completes a session and sees her own waveforms — with NO GPU involved
 * at all". A build that cannot do that without spark-1 has built the seam wrong.
 */
export type TtsMode = "live" | "none";

/**
 * The three job lanes.
 *
 *   score  the sidecar on amd-server — ECAPA, WavLM-sv, UTMOSv2, Perth, diarisation. CPU/ROCm-local.
 *   clone  `POST /voices` on spark-1, then the identity-by-generation check (VOX-LAW 8).
 *   asr    Nemotron on spark-1 :8006 — the consent transcript and the per-take advisory one.
 */
export type JobLane = "score" | "clone" | "asr";
export const JOB_LANES: readonly JobLane[] = ["score", "clone", "asr"];

/**
 * How many jobs of each lane may be `running` at once, across every worker.
 *
 * `score` is 2: amd-server has 16 cores, the sidecar runs `torch.set_num_threads(1)` (§6.6, so the
 * envelope is reproducible), and two scoring jobs at one thread each leave the box responsive for the
 * origin and the worker. It is deliberately not higher — the sidecar loads several models and a third
 * copy is memory this box also hands to the ROCm venv.
 *
 * `clone` and `asr` are 1 each because spark-1 **serialises anyway**: three concurrent Chatterbox
 * requests returned at 5.8 / 9.3 / 12.9 s (§5.6), i.e. identical total wall time. A second slot buys
 * nothing and costs the priority queue its meaning.
 *
 * ⚠️ **THIS TABLE IS NOT THE ADMISSION CONTROL, AND IT CANNOT BE.** `clone: 1` plus `asr: 1` is two
 * concurrent requests on one GPU, and audiobook's worker on the same tailnet is a third. **VOX-LAW
 * 30: there is one admission token for spark-1, held by a service on amd-server, and every process
 * that generates on that host acquires it.** Priority order is interactive `ensure` > studio reveal >
 * studio preview > bake-off preview > canary > `autoNarrate` sweep > §7 generation. audiobook's own
 * `shared/config.ts` documents `ttsInflight` as *"One GPU, one semaphore"* — and that comment assumes
 * audiobook is the only client, which stopped being true the moment this repo existed.
 *
 * It lives in `shared/` because in audiobook the same table was written down twice and the two copies
 * disagreed (`media: 3` in the server, `media: 2` in the worker). The server's copy is baked into the
 * SQL that enforces the lane ceiling and the worker's sizes its slot pool; they are the same number
 * by definition. No I/O here, so `shared/` can hold it.
 */
export const LANE_CONCURRENCY: Readonly<Record<JobLane, number>> = Object.freeze({
  score: 2,
  clone: 1,
  asr: 1,
});

/**
 * A Chatterbox host and the LOCAL voice ids its remote voice names map to.
 *
 * The mapping is the whole point, and it is inherited verbatim from audiobook (`shared/config.ts:
 * 60-62`) because the reasoning is identical and the consequence here is worse. spark-1 and metal
 * both serve a voice called "karti" and they are DIFFERENT CLONES. **A host serving a different clone
 * is a different `voiceId`, so failover between them is not expressible — which is correct.** With a
 * content-addressed store, failing over writes bytes under a key derived from the wrong voice and
 * permanently poisons the store with mixed-provenance audio no future lookup can distinguish. That is
 * not a seam. It is corruption.
 *
 * **There is deliberately nothing on `metal` at all (§12.2, §15's deferred list.)** If you are adding
 * a fallback host because spark-1 is down, you are about to mint audio from a different person's
 * voice model.
 */
export interface TtsHost {
  url: string;
  /** remote voice name → our voiceId, e.g. { "mom-r3": "mom@vox" } */
  voices: Record<string, string>;
}

export interface SupabaseConfig {
  /**
   * `false` IS A SUPPORTED STATE — the app boots and falls back to local scrypt, which is what keeps
   * CI and a fresh clone working.
   *
   * ⚠️ Local dev MUST use the fallback: leave the three VOX_SUPABASE_* unset. There is ONE production
   * Supabase project (`gttcxopjeswqyumedhlj`, **not** the lumbridge project) shared by six karti.ai
   * sites; exporting its credentials on a laptop means test signups write real auth users and a local
   * account deletion deletes a real one. §11.1: public signup is **closed**, Supabase verifies
   * passwords and holds nothing else, the app mints its own signed httpOnly cookie, and admin is
   * derived live from `VOX_ADMIN_EMAILS`.
   */
  enabled: boolean;
  url: string | null;
  publishableKey: string | null;
  serviceRoleKey: string | null;
}

export interface Config {
  env: VoxEnv;
  isProd: boolean;
  logLevel: string;

  // ── cloud-2: the app ──
  port: number;
  host: string;
  /** Absolute URL the browser sees. Cookies, claim links and the manifest's self-reference use it. */
  publicOrigin: string;
  /** SQLite. Gitignored, and THE ONLY THING ON CLOUD-2 THAT MUST SURVIVE A DEPLOY. */
  dataDir: string;

  // ── amd-server: the origin, the workers and the sidecar ──
  /** Where takes, seeds, consent recordings and previews live. 852 G free; cloud-2 has 56 G. */
  storageDir: string;
  /**
   * A WORKER and PROXY variable, and it is a TAILNET IP on purpose: `amd-server` does not resolve
   * from cloud-2 and vice versa. A hostname here is how a deploy passes on the box you wrote it on
   * and fails on the box it runs on.
   */
  originUrl: string;
  originPort: number;
  originBind: string;
  /** Bearer for `/internal/*` and the origin blob service. Tailnet only. Never in argv. */
  originToken: string | null;
  workerId: string;
  workerLanes: JobLane[];
  /** The scoring sidecar (§6.5). **NEVER on spark-1** — see the field's warning below. */
  sidecarUrl: string;

  // ── identity ──
  /** Signs the `vox` cookie. Missing ⇒ the server mints an ephemeral one and logs it as degraded. */
  cookieSecret: string | null;
  /** HMAC over the canonicalised fleet manifest (§9.2, VOX-LAW 24). */
  manifestKey: string | null;
  inviteCode: string;
  /** Normalised (trimmed, lowercased). Godmode is derived from membership, live, per request. */
  adminEmails: ReadonlySet<string>;
  supabase: SupabaseConfig;
  /** Private Buzz workflow for durable signup/login notices. Both values or neither. */
  buzzAuthWebhookUrl: string | null;
  buzzAuthWebhookSecret: string | null;
  /** Who the family-facing copy's `{helper}` is on this deployment. */
  helperName: string;

  // ── spark-1 ──
  ttsMode: TtsMode;
  ttsHosts: TtsHost[];
  asrUrl: string;
  asrLang: string;
  /** VOX-LAW 30's daily ceiling. Mirrors audiobook's `autoNarrateGpuMinPerDay` on the same GPU. */
  gpuMinPerDay: number;

  // ── budgets (§11.3) ──
  /** Raw recorded bytes, per `actorKey`. Not per user: our highest-volume writer has no users row. */
  anonTakeBytes: number;
  userTakeBytes: number;
  /** GPU seconds, per `actorKey`, per day. */
  anonGpuSeconds: number;
  userGpuSeconds: number;
  maxUploadBytes: number;
  previewPerHour: number;

  /** Degraded-but-running states. Printed at boot and surfaced on /api/v1/status. Never silent. */
  warnings: string[];
}

export class ConfigError extends Error {
  override name = "ConfigError";
}

// ─── parsing helpers ─────────────────────────────────────────────────────────

type Env = Readonly<Record<string, string | undefined>>;

const raw = (env: Env, key: string): string | undefined => {
  const v = env[key];
  if (v === undefined) return undefined;
  const t = v.trim();
  return t === "" ? undefined : t;
};

const str = (env: Env, key: string, dflt: string): string => raw(env, key) ?? dflt;

const optional = (env: Env, key: string): string | null => raw(env, key) ?? null;

function int(env: Env, key: string, dflt: number, min: number, max: number): number {
  const v = raw(env, key);
  if (v === undefined) return dflt;
  const n = Number(v);
  if (!Number.isInteger(n) || n < min || n > max) {
    throw new ConfigError(`${key}: expected an integer in [${min}, ${max}], got ${JSON.stringify(v)}`);
  }
  return n;
}

function oneOf<T extends string>(env: Env, key: string, dflt: T, allowed: readonly T[]): T {
  const v = raw(env, key);
  if (v === undefined) return dflt;
  if (!(allowed as readonly string[]).includes(v)) {
    throw new ConfigError(`${key}: expected one of ${allowed.join("|")}, got ${JSON.stringify(v)}`);
  }
  return v as T;
}

const listOf = (env: Env, key: string): string[] =>
  (raw(env, key) ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");

// ─── the known keys ──────────────────────────────────────────────────────────

/**
 * The complete set. Anything else beginning with `VOX_` is a typo or a stale variable, and either way
 * it is somebody's belief about a setting that is not in effect. Keep it sorted, and keep
 * `.env.example` in step — the two are diffed by eye at every deploy.
 */
export const KNOWN_KEYS: readonly string[] = [
  "VOX_ADMIN_EMAILS",
  "VOX_ANON_GPU_SECONDS",
  "VOX_ANON_TAKE_BYTES",
  "VOX_ASR_LANG",
  "VOX_ASR_URL",
  "VOX_BUZZ_AUTH_WEBHOOK_SECRET",
  "VOX_BUZZ_AUTH_WEBHOOK_URL",
  "VOX_COOKIE_SECRET",
  "VOX_DATA_DIR",
  "VOX_ENV",
  "VOX_GPU_MIN_PER_DAY",
  "VOX_HELPER_NAME",
  "VOX_HOST",
  "VOX_INVITE_CODE",
  "VOX_LOG_LEVEL",
  "VOX_MANIFEST_KEY",
  "VOX_MAX_UPLOAD_BYTES",
  "VOX_ORIGIN_BIND",
  "VOX_ORIGIN_PORT",
  "VOX_ORIGIN_TOKEN",
  "VOX_ORIGIN_URL",
  "VOX_PORT",
  "VOX_PREVIEW_HOUR",
  "VOX_PUBLIC_ORIGIN",
  "VOX_SIDECAR_URL",
  "VOX_STORAGE_DIR",
  "VOX_SUPABASE_PUBLISHABLE_KEY",
  "VOX_SUPABASE_SERVICE_ROLE_KEY",
  "VOX_SUPABASE_URL",
  "VOX_TTS_HOSTS",
  "VOX_TTS_MODE",
  "VOX_TTS_URL",
  "VOX_USER_GPU_SECONDS",
  "VOX_USER_TAKE_BYTES",
  "VOX_WORKER_ID",
  "VOX_WORKER_LANES",
];

const KNOWN = new Set(KNOWN_KEYS);

// ─── the boxes ───────────────────────────────────────────────────────────────

/**
 * Tailnet addresses, written down once. §12.1.
 *
 * These are defaults, not constants — every one is overridable by a `VOX_*` variable — but they are
 * here rather than inline in `loadConfig` because they are the four facts a new agent asks for first,
 * and because an IP typed twice is an IP that will be edited once.
 */
export const BOXES = Object.freeze({
  /** aarch64, tailnet only, 56 G free at 72%. The app, the SQLite, the queue, the client bundle. */
  cloud2: "100.92.185.76",
  /** 16 cores, 852 G free on /mnt/storage. Origin, worker, sidecar, the admission token service. */
  amdServer: "100.119.208.107",
  /** The GPU, under KUDA. Chatterbox :8095, Nemotron ASR :8006. Down ⇒ deferred reveal, never a failure. */
  spark1: "100.127.247.67",
  /** A DIFFERENT clone under the same name. **Never a fallback** (§2.2). Recorded so nobody "adds" it. */
  metal: "100.123.204.85",
});

// ─── the loader ──────────────────────────────────────────────────────────────

export function loadConfig(env: Env): Config {
  const unknown = Object.keys(env)
    .filter((k) => k.startsWith("VOX_") && !KNOWN.has(k))
    .sort();
  if (unknown.length > 0) {
    throw new ConfigError(
      `unknown VOX_* variable${unknown.length > 1 ? "s" : ""}: ${unknown.join(", ")}\n` +
        `A variable that is set but not read is a setting somebody believes is on. ` +
        `Add it to KNOWN_KEYS in shared/config.ts (and to .env.example) or remove it.\n` +
        `Known: ${KNOWN_KEYS.join(", ")}`,
    );
  }

  const warnings: string[] = [];

  // NODE_ENV is honoured as a fallback (it is what `vite build` and most tooling set) but VOX_ENV
  // wins, so the one namespace stays authoritative.
  const envName = oneOf<VoxEnv>(env, "VOX_ENV", env["NODE_ENV"] === "production" ? "prod" : "dev", ["dev", "prod"]);

  const ttsMode = oneOf<TtsMode>(env, "VOX_TTS_MODE", "live", ["live", "none"]);
  const ttsHosts = parseTtsHosts(env);
  if (ttsMode === "none") {
    // Not a warning about a mistake — a warning about a product state, so that "why did nobody get a
    // voice today" has an answer in the boot log and on /api/v1/status.
    warnings.push(
      "VOX_TTS_MODE=none — no voice will be produced. The studio records normally and every session " +
        "ends on the deferred path (§3.14), which is a designed outcome and not a failure. This is " +
        "the correct setting while spark-1 is down.",
    );
  }

  const supabase = parseSupabase(env, warnings);

  const cookieSecret = optional(env, "VOX_COOKIE_SECRET");
  if (cookieSecret === null) {
    warnings.push(
      "VOX_COOKIE_SECRET is unset — the guest cookie is signed with an ephemeral key and every " +
        "restart orphans every in-flight session from its browser. The claim token still resolves " +
        "(VOX-LAW 20), which is exactly what it is for, but set this in /etc/voice-karti/env.",
    );
  }
  const manifestKey = optional(env, "VOX_MANIFEST_KEY");
  if (manifestKey === null) {
    warnings.push("VOX_MANIFEST_KEY is unset — GET /api/v1/manifest will refuse to sign, so no push can be verified (§9.2).");
  }
  const originToken = optional(env, "VOX_ORIGIN_TOKEN");
  if (originToken === null) {
    warnings.push("VOX_ORIGIN_TOKEN is unset — /internal/* and the origin blob service will refuse every call.");
  }

  const adminEmails = new Set(listOf(env, "VOX_ADMIN_EMAILS").map((e) => e.toLowerCase()));
  if (adminEmails.size === 0) {
    warnings.push(
      "VOX_ADMIN_EMAILS is empty — nobody has godmode, so /admin is unreachable and no consent take " +
        "can be approved by hand. §17 Q5's deceased-relative path needs a name on the approval row.",
    );
  }

  const buzzAuthWebhookUrl = optional(env, "VOX_BUZZ_AUTH_WEBHOOK_URL");
  const buzzAuthWebhookSecret = optional(env, "VOX_BUZZ_AUTH_WEBHOOK_SECRET");
  if ((buzzAuthWebhookUrl === null) !== (buzzAuthWebhookSecret === null)) {
    warnings.push(
      "VOX_BUZZ_AUTH_WEBHOOK_URL and VOX_BUZZ_AUTH_WEBHOOK_SECRET must be set together — " +
        "signup/login Buzz notices are disabled.",
    );
  }

  const laneNames = listOf(env, "VOX_WORKER_LANES");
  const workerLanes = (laneNames.length > 0 ? laneNames : [...JOB_LANES]) as JobLane[];
  for (const lane of workerLanes) {
    if (!JOB_LANES.includes(lane)) {
      throw new ConfigError(`VOX_WORKER_LANES: unknown lane ${JSON.stringify(lane)}; known: ${JOB_LANES.join(", ")}`);
    }
  }

  const sidecarUrl = str(env, "VOX_SIDECAR_URL", `http://${BOXES.amdServer}:8963`).replace(/\/+$/, "");
  if (sidecarUrl.includes(BOXES.spark1)) {
    /**
     * ⚠️ A REFUSAL, NOT A WARNING (§6.5). spark-1's GPU is contended by a 40 GB vLLM and a watchdog
     * that stops Chatterbox when `MemAvailable` falls. Putting ECAPA, WavLM-sv, UTMOSv2 and Perth on
     * it would make this product the cause of its own outages — and of audiobook's, because the same
     * watchdog takes narration down with it.
     */
    throw new ConfigError(
      `VOX_SIDECAR_URL points at spark-1 (${BOXES.spark1}). The scorer NEVER runs there (§6.5): that ` +
        `GPU is shared with a 40 GB vLLM and a watchdog that stops Chatterbox under memory pressure, ` +
        `so an embedding model on it makes this product the cause of its own outages. It runs on ` +
        `amd-server under \`systemd --user\`.`,
    );
  }

  const cfg: Config = {
    env: envName,
    isProd: envName === "prod",
    logLevel: str(env, "VOX_LOG_LEVEL", "info"),

    // §12.3: 8902–8911 are taken on cloud-2; 8912 asserted free with `ss -ltn`, and asserted again in
    // the runbook before every deploy. Also written in vite.config.ts for the dev proxy; the two are
    // checked against each other by test/wiring.test.ts.
    port: int(env, "VOX_PORT", 8912, 1, 65535),
    host: str(env, "VOX_HOST", "127.0.0.1"),
    publicOrigin: str(env, "VOX_PUBLIC_ORIGIN", "http://localhost:8912").replace(/\/+$/, ""),
    dataDir: str(env, "VOX_DATA_DIR", "data"),

    storageDir: str(env, "VOX_STORAGE_DIR", "/mnt/storage/voice"),
    // §12.3: on amd-server 8080, 8115, 8445–8447, 8906 and 8961 are bound; 8962 is free. 8961 is
    // audiobook's origin — this is the same box, so the number is not arbitrary and not swappable.
    originUrl: str(env, "VOX_ORIGIN_URL", `http://${BOXES.amdServer}:8962`).replace(/\/+$/, ""),
    originPort: int(env, "VOX_ORIGIN_PORT", 8962, 1, 65535),
    originBind: str(env, "VOX_ORIGIN_BIND", BOXES.amdServer),
    originToken,
    workerId: str(env, "VOX_WORKER_ID", "worker-1"),
    workerLanes,
    sidecarUrl,

    cookieSecret,
    manifestKey,
    inviteCode: str(env, "VOX_INVITE_CODE", "115karti"),
    adminEmails,
    supabase,
    buzzAuthWebhookUrl:
      buzzAuthWebhookUrl !== null && buzzAuthWebhookSecret !== null
        ? buzzAuthWebhookUrl.replace(/\/+$/, "")
        : null,
    buzzAuthWebhookSecret:
      buzzAuthWebhookUrl !== null && buzzAuthWebhookSecret !== null ? buzzAuthWebhookSecret : null,
    helperName: str(env, "VOX_HELPER_NAME", "Karti"),

    ttsMode,
    ttsHosts,
    asrUrl: str(env, "VOX_ASR_URL", `http://${BOXES.spark1}:8006`).replace(/\/+$/, ""),
    // §3.8: the language passed to /asr is `en` for the consent line BY DEFAULT and is a column, not
    // a constant — the unscripted prompt may be Hindi or Gujarati and that is a better seed.
    asrLang: str(env, "VOX_ASR_LANG", "en"),
    // Mirrors audiobook's AB_AUTO_NARRATE_GPU_MIN_PER_DAY on the SAME GPU. 90 min/day is what that
    // box already budgets for a background sweep; this repo's share sits inside VOX-LAW 30's token,
    // behind the interactive lanes.
    gpuMinPerDay: int(env, "VOX_GPU_MIN_PER_DAY", 90, 0, 1440),

    /**
     * §11.3. Two counters, `takeBytes` and `gpuSeconds`, keyed on `actorKey` — audiobook's
     * `server/src/quota.ts` sums `DISTINCT artifactSha` page-audio bytes against `users.quotaBytes`,
     * and our highest-volume writer is an ANONYMOUS actor with no `users` row at all, so the lifted
     * SQL has no subject for the primary flow.
     *
     * 2 GiB for an anon actor: §12.2 measures a long session at ~52 MB of recordings plus ~6.5 MB of
     * seeds and ~5 MB of previews, so ~2 GiB is roughly thirty full sessions from one browser —
     * generous for a family, finite against a script. 20 GiB for a signed-in user is ten times that
     * against 852 G free.
     *
     * ⚠️ **The ceiling is a coached message, never a refusal mid-session (VOX-LAW 15).** A quota that
     * stops a recording halfway is a refusal wearing a budget's clothes.
     */
    anonTakeBytes: int(env, "VOX_ANON_TAKE_BYTES", 2_147_483_648, 0, Number.MAX_SAFE_INTEGER),
    userTakeBytes: int(env, "VOX_USER_TAKE_BYTES", 21_474_836_480, 0, Number.MAX_SAFE_INTEGER),
    // One reveal is ~2.2 s of GPU at the measured ~3.7× realtime (§5.6). 300 s/day is ~130 reveals or
    // ~70 previews for an anon actor; 3600 for a signed-in one covers a bake-off plus a §7 arm.
    anonGpuSeconds: int(env, "VOX_ANON_GPU_SECONDS", 300, 0, 86_400),
    userGpuSeconds: int(env, "VOX_USER_GPU_SECONDS", 3600, 0, 86_400),
    // One take at 24-bit/48 kHz mono is 144 kB/s, so a 60 s ramble is ~8.6 MB. 64 MB is seven times
    // the longest recording this product asks for and still refuses a file that is not a take.
    // Caddy's own `request_body max_size` is 128MB (§12.3) and must stay above this.
    maxUploadBytes: int(env, "VOX_MAX_UPLOAD_BYTES", 67_108_864, 0, Number.MAX_SAFE_INTEGER),
    previewPerHour: int(env, "VOX_PREVIEW_HOUR", 30, 0, 100_000),

    warnings,
  };

  if (cfg.isProd && !cfg.publicOrigin.startsWith("https://")) {
    // The `vox` cookie is `secure` in prod; over http it is set and never sent back, which reads as
    // "signing in does nothing" with a 200 on every request.
    warnings.push(`VOX_PUBLIC_ORIGIN is ${cfg.publicOrigin} in prod — the secure cookie will never come back.`);
  }

  /**
   * ⚠️ **A loopback `VOX_HOST` in prod means no recording is ever scored, and nothing errors.**
   *
   * The worker runs on amd-server and dials cloud-2's `:8912` **directly over the tailnet** — it must,
   * because `/internal/*` refuses by design any request carrying `X-Forwarded-For`, i.e. anything that
   * came through Caddy. So the process has to be listening on the tailnet interface. Bound to
   * `127.0.0.1` it is not, and `POST /internal/jobs/claim` is ECONNREFUSED forever: every session
   * completes, every take uploads, and every voice sits in `scoring` with a silent server log.
   *
   * A warning rather than a refusal because a single-box install with the worker on localhost is a
   * legitimate configuration — it is how this repo is developed.
   */
  if (cfg.isProd && cfg.originToken !== null && isLoopbackHost(cfg.host)) {
    warnings.push(
      `VOX_HOST=${cfg.host} binds loopback only, but /internal/* is enabled (VOX_ORIGIN_TOKEN is set). ` +
        "A worker on another box cannot reach the queue — it must connect over the tailnet, because " +
        `/internal/* refuses anything forwarded by Caddy. Set VOX_HOST to this box's tailnet address ` +
        `(cloud-2: ${BOXES.cloud2}) unless the worker runs on this machine.`,
    );
  }

  return Object.freeze(cfg);
}

/**
 * ⚠️ **THE METAL REFUSAL IS AT THE SINGLE EXIT, AND IT IS THERE BECAUSE IT WAS NOT.**
 *
 * This function used to be one function with two `return`s, and the `BOXES.metal` check lived inside
 * the `VOX_TTS_HOSTS` loop. So the JSON branch refused metal and **the one-line shorthand did not**:
 * `VOX_TTS_URL=http://100.123.204.85:8790` booted clean, with `ttsHosts` pointing at metal and no
 * warning anywhere. That is the exact configuration AGENTS.md calls out as a refusal ("there is
 * deliberately no failover to metal, and `shared/config.ts` REFUSES TO BOOT if you configure one")
 * and §2.2 calls **corruption rather than misconfiguration** — metal holds a *different* clone under
 * the same name, so generating from it mints audio from another person's model, under our voiceId,
 * into a content-addressed store where no future lookup can tell the two apart.
 *
 * And the shorthand is the *likelier* of the two branches to be wrong: it is one line in an env file,
 * it is what a laptop sets, and it is what somebody types at 2 a.m. when spark-1 is down and metal is
 * up. A guard that covers only the elaborate path guards the case that was never going to happen.
 *
 * So: `readTtsHosts` parses, `parseTtsHosts` validates, and **every host that leaves this file passes
 * through the same loop.** A new source of hosts (a config file, a discovery probe) gets the refusal
 * for free by construction rather than by somebody remembering. `where` is carried per host so the
 * message names the variable the operator actually set — `VOX_TTS_URL` or `VOX_TTS_HOSTS[2].url` —
 * because "one of your TTS hosts is metal" is not an actionable sentence in an env file with both.
 */
function parseTtsHosts(env: Env): TtsHost[] {
  const sources = readTtsHosts(env);

  for (const { host, where } of sources) {
    if (host.url.includes(BOXES.metal)) {
      // §2.2. Not a warning: metal holds a DIFFERENT clone under the same name, and there is
      // deliberately no failover. Generating from it would mint audio from another person's model.
      throw new ConfigError(
        `${where} is metal (${BOXES.metal}). metal serves a DIFFERENT clone under the same name ` +
          `(§2.2). A host serving a different clone is a different voiceId, and there is no failover ` +
          `by design — falling back to it permanently poisons a content-addressed store with ` +
          `mixed-provenance audio no future lookup can distinguish.`,
      );
    }
  }

  return sources.map((s) => s.host);
}

/** A parsed host and the variable it came from, so a refusal can name it. See `parseTtsHosts`. */
interface TtsHostSource {
  host: TtsHost;
  where: string;
}

/**
 * Parsing only — **no policy.** Everything this returns is validated by `parseTtsHosts`, which is the
 * only caller and the only exit. Shape errors stay here because they are per-branch; the metal
 * refusal is not, and putting it here is how it came to cover one branch out of two.
 */
function readTtsHosts(env: Env): TtsHostSource[] {
  const json = raw(env, "VOX_TTS_HOSTS");
  if (json === undefined) {
    // The single-host shorthand, and what a laptop sets. VOX_TTS_HOSTS is the real mechanism and wins
    // when both are present, because only it can express the remote-name → voiceId mapping.
    const url = str(env, "VOX_TTS_URL", `http://${BOXES.spark1}:8095`).replace(/\/+$/, "");
    return [{ host: { url, voices: {} }, where: "VOX_TTS_URL" }];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    throw new ConfigError(`VOX_TTS_HOSTS: not valid JSON — ${(e as Error).message}`);
  }
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new ConfigError("VOX_TTS_HOSTS: expected a non-empty JSON array of { url, voices }");
  }

  const hosts: TtsHostSource[] = [];
  for (const [i, h] of parsed.entries()) {
    const o = h as Partial<TtsHost>;
    if (typeof o?.url !== "string" || o.url === "") {
      throw new ConfigError(`VOX_TTS_HOSTS[${i}]: missing "url"`);
    }
    if (typeof o.voices !== "object" || o.voices === null) {
      throw new ConfigError(`VOX_TTS_HOSTS[${i}]: missing "voices" — a host with no voice map cannot be resolved`);
    }
    for (const [remote, id] of Object.entries(o.voices)) {
      if (typeof id !== "string" || id === "") {
        throw new ConfigError(`VOX_TTS_HOSTS[${i}].voices.${remote}: expected a voiceId string`);
      }
    }
    hosts.push({
      host: { url: o.url.replace(/\/+$/, ""), voices: { ...(o.voices as Record<string, string>) } },
      where: `VOX_TTS_HOSTS[${i}].url`,
    });
  }
  return hosts;
}

function parseSupabase(env: Env, warnings: string[]): SupabaseConfig {
  const url = optional(env, "VOX_SUPABASE_URL");
  const publishableKey = optional(env, "VOX_SUPABASE_PUBLISHABLE_KEY");
  const serviceRoleKey = optional(env, "VOX_SUPABASE_SERVICE_ROLE_KEY");
  const present = [url, publishableKey, serviceRoleKey].filter((v) => v !== null).length;

  if (present === 0) {
    // The intended state on a laptop and in CI. Not a warning — see the header of SupabaseConfig.
    return { enabled: false, url: null, publishableKey: null, serviceRoleKey: null };
  }
  if (present < 3) {
    // Degrade rather than die (the EnvironmentFile rule), but say so — a half-configured Supabase
    // that silently falls back to scrypt is somebody logging in against the wrong credential store,
    // on a project six sites share.
    warnings.push(
      "VOX_SUPABASE_* is partially set: all three of VOX_SUPABASE_URL, VOX_SUPABASE_PUBLISHABLE_KEY " +
        "and VOX_SUPABASE_SERVICE_ROLE_KEY are required. Falling back to local scrypt.",
    );
    return { enabled: false, url: null, publishableKey: null, serviceRoleKey: null };
  }
  return { enabled: true, url, publishableKey, serviceRoleKey };
}

// ─── the process-wide singleton (server / worker / origin / cli only) ────────

let cached: Config | null = null;

/**
 * Memoised config for the Node processes. NEVER call this from client code — `shared/` is bundled by
 * Vite and `process` does not exist in a browser. The client learns everything it needs from
 * `GET /api/v1/session`.
 */
export function getConfig(): Config {
  if (cached !== null) return cached;
  if (typeof process === "undefined") {
    throw new ConfigError(
      "getConfig() was called without a process environment — the client must not read VOX_* config. " +
        "What the browser needs is on SessionEnvelope.app (shared/protocol.ts).",
    );
  }
  cached = loadConfig(process.env);
  return cached;
}

/** Tests only: drop the memo so a second `loadConfig` sees a different environment. */
export function resetConfigForTests(): void {
  cached = null;
}

/** Loopback in either family, plus the hostname form. Used by the VOX_HOST prod warning above. */
function isLoopbackHost(host: string): boolean {
  const h = host.trim().toLowerCase().replace(/^\[|\]$/g, "");
  return h === "localhost" || h === "::1" || h === "127.0.0.1" || h.startsWith("127.");
}
